'use client'

import { useState, useEffect, useCallback } from 'react'
import { createBrowserClient } from '@supabase/ssr'
import type { Permission } from '@/lib/permissions.shared'

interface UsePermissionsReturn {
  permissions: Permission[]
  isLoading: boolean
  error: Error | null
  hasPermission: (permission: Permission) => boolean
  hasAnyPermission: (permissions: Permission[]) => boolean
  refresh: () => Promise<void>
}

/**
 * Client-side hook to fetch and check user permissions from Supabase.
 * Merges role-based permissions with user-specific grants.
 */
export function usePermissions(userRole: string | null, userId?: string | null): UsePermissionsReturn {
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchPermissions = useCallback(async () => {
    if (!userRole && !userId) {
      setPermissions([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const supabase = createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
      )

      // Fetch role-based permissions
      const rolePermsPromise = userRole
        ? supabase.from('role_permissions').select('permission').eq('role', userRole)
        : Promise.resolve({ data: [], error: null })

      // Fetch user-specific permissions (with action: grant/deny)
      const userPermsPromise = userId
        ? supabase.from('user_permissions').select('permission, action').eq('user_id', userId)
        : Promise.resolve({ data: [], error: null })

      const [roleResult, userResult] = await Promise.all([rolePermsPromise, userPermsPromise])

      if (roleResult.error) throw new Error(roleResult.error.message)
      if (userResult.error) throw new Error(userResult.error.message)

      const rolePerms = (roleResult.data || []).map(p => p.permission as Permission)
      const userGrants = (userResult.data || []).filter(p => p.action === 'grant').map(p => p.permission as Permission)
      const userDenials = new Set((userResult.data || []).filter(p => p.action === 'deny').map(p => p.permission as Permission))

      // Start with role + grants, then remove denials
      const effective = new Set([...rolePerms, ...userGrants])
      for (const denied of userDenials) {
        effective.delete(denied)
      }

      setPermissions([...effective])
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch permissions'))
      setPermissions([])
    } finally {
      setIsLoading(false)
    }
  }, [userRole, userId])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.includes(permission)
    },
    [permissions]
  )

  const hasAnyPermission = useCallback(
    (perms: Permission[]): boolean => {
      return perms.some(p => permissions.includes(p))
    },
    [permissions]
  )

  return {
    permissions,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    refresh: fetchPermissions,
  }
}
